import { useEffect, useState } from 'react';
import { useUser, useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api/apiClient';
import DashboardSkeleton from './DashboardSkeleton';

function Onboarding() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const completeOnboarding = async () => {
      // Wait for everything to be loaded
      if (!isLoaded || !user || !getToken || isSettingUp) return;

      // Check if already has role
      if (user.role) {
        navigate('/dashboard', { replace: true });
        return;
      }

      setIsSettingUp(true);

      try {
        // Get auth token
        const token = await getToken();
        
        // Set auth headers
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Call backend to complete onboarding
        const response = await apiClient.post('/users/complete-onboarding', {
          signupOrigin: window.location.origin
        });

        if (response.data.success) {
          // Reload user to get updated metadata
          await user.reload();
          
          // Navigate to dashboard
          navigate('/dashboard', { replace: true });
        } else {
          throw new Error('Onboarding failed');
        }
        
      } catch (error) {
        console.error('Onboarding error:', error);
        setError(error.message);
        
        // Still navigate to dashboard after a delay to not block user
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 3000);
      }
    };

    completeOnboarding();
  }, [isLoaded, user, getToken, navigate, isSettingUp]);

  // Show error state if needed
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900">Setup Issue</h2>
            <p className="mt-2 text-gray-600">There was a problem setting up your account.</p>
            <p className="mt-2 text-sm text-gray-500">Redirecting to dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard skeleton loader
  return <DashboardSkeleton />;
}

export default Onboarding;